import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  $accountCardListStore,
  $transferReasonsStore,
  $transferRequestsCreateStore,
  $transferRequestsFeeStore,
  $transferRequestsGetFxRateStore,
  $transferRequestsUpdateStore,
} from '@lib/store';
import { AmountUtils, cn, PaletteUtils, PaymentUtils } from '@shared/utils';
import { type AccountResponse, AccountType, AmountTypeEnum, TransferRecipientTypeEnum, TransferRequestTypeEnum } from '@shared/api';
import { FilesFormBlock, FilesFormData, PaymentFormBlock, PaymentFormData, SelectAccount, TransferConfirmationBlock } from '@widgets';
import { Button, Spinner } from '@shared/widgets';
import { Constants } from '@shared/constants';

type OwnAccountFormData = { accountTo?: AccountResponse } & PaymentFormData & FilesFormData;
const DEFAULT_VALUES: OwnAccountFormData = {
  accountTo: undefined,
  amountType: AmountTypeEnum.BeneficiaryAmount,
  amount: undefined,
  beneficiaryAmount: undefined,
  paymentReason: '',
  purpose: '',
  files: [],
};

interface OwnAccountTabProps {
  account: AccountResponse | null;
}

export const OwnAccountTab = (props: OwnAccountTabProps) => {
  const transferReasonsStore = useUnit($transferReasonsStore.store);
  const accountStore = useUnit($accountCardListStore.store);
  const feeStore = useUnit($transferRequestsFeeStore.store);
  const transferRequestsCreateStore = useUnit($transferRequestsCreateStore.store);
  const transferRequestsUpdateStore = useUnit($transferRequestsUpdateStore.store);

  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);

  const { t } = useTranslation('payments');

  const formId = useId();
  const formControl = useForm<OwnAccountFormData>({ defaultValues: DEFAULT_VALUES });
  const { control, formState, handleSubmit, watch, trigger, setValue } = formControl;
  const accountTo = watch('accountTo');
  const amount = watch('amount');
  const beneficiaryAmount = watch('beneficiaryAmount');

  useEffect(() => {
    $accountCardListStore.events.getAccounts({ AccountType: AccountType.Payment });

    return () => {
      $accountCardListStore.events.clear();
      $transferRequestsGetFxRateStore.events.clear();
      $transferRequestsCreateStore.events.clear();
      $transferRequestsUpdateStore.events.clear();
    };
  }, []);

  const requestFeeHandler = useCallback(() => {
    if (!amount || !accountTo) return;
    $transferRequestsFeeStore.events.getFee({
      AccountId: props.account?.id,
      SenderAmount: amount,
      FastPayment: true,
      BeneficiaryCurrencyCode: accountTo?.currency,
      BeneficiaryBankCountryCode: 'US',
      TransferRequestType: TransferRequestTypeEnum.BetweenInsigniaAccount,
    });
  }, [accountTo?.currency, amount, props.account?.id]);
  useEffect(() => {
    requestFeeHandler();
    return () => $transferRequestsFeeStore.events.clear();
  }, [props.account, accountTo]);

  useEffect(() => {
    if (props.account?.currency && accountTo?.currency && props.account?.id) {
      if (props.account.currency === accountTo.currency) {
        $transferRequestsGetFxRateStore.events.setRate(1);
        return;
      }
      $transferRequestsGetFxRateStore.events.getFxRate({
        AccountId: props.account?.id,
        FromCurrency: props.account?.currency,
        ToCurrency: accountTo?.currency,
        CountryCode: 'DE',
      });
    }
  }, [accountTo?.currency, props.account?.currency]);

  const submitHandler = async (data: OwnAccountFormData) => {
    if (!props.account || !data.accountTo) return;

    const isValid = await trigger();
    if (!isValid) return;

    if (transferRequestsCreateStore?.data) {
      await PaymentUtils.updateTransfer({
        account: props.account,
        accountTo: data.accountTo,
        payment: { ...data, id: transferRequestsCreateStore.data.id, type: TransferRequestTypeEnum.BetweenInsigniaAccount },
      });
      if (transferRequestsCreateStore.data.id && data.files.length) {
        const files = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: transferRequestsCreateStore.data.id, files: data.files });
        setValue('files', files);
      }
      setShowPaymentModal(true);
      return;
    }

    const transfer = await PaymentUtils.createTransfer({
      account: props.account,
      accountTo: data.accountTo,
      payment: { ...data, type: TransferRequestTypeEnum.BetweenInsigniaAccount },
    });
    if (transfer.data?.id && data.files.length) {
      const files = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: transfer.data.id, files: data.files });
      setValue('files', files);
    }
    setShowPaymentModal(true);
  };

  const reasonOptions = useMemo(() => {
    const bankCountryCode = Constants.BANK_COUNTRY_CODE;
    const individualReasons = transferReasonsStore.data.filter((reason) => reason?.beneficiaryType?.includes(TransferRecipientTypeEnum.Business));
    const countryReasons = individualReasons.filter((reason) => reason.countryCode?.toUpperCase() == bankCountryCode);
    const reasons = countryReasons.length > 0 ? countryReasons : individualReasons.filter((reason) => !reason.countryCode);
    return reasons.map((reason) => ({ label: reason.name, value: reason.id }));
  }, [transferReasonsStore.data]);
  return (
    <FormProvider {...formControl}>
      <Spinner show={formState.isSubmitting} position />
      <div className={cn(PaletteUtils.isInsigniaPalette && 'mt-6', PaletteUtils.isVcfcPalette && 'bg-white dark:bg-soft-black')}>
        <form id={formId} noValidate onSubmit={handleSubmit(submitHandler)}>
          <div className='bg-white p-6 dark:bg-soft-black'>
            <Controller
              name='accountTo'
              control={control}
              rules={{ required: t('errors.form.required', { ns: 'common' }) }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <SelectAccount
                  label={t('betweenAccounts.placeholders.transferTo')}
                  loading={accountStore.loading}
                  type={AccountType.Payment}
                  accounts={accountStore?.data?.filter((account) => account.id !== props.account?.id)}
                  error={error?.message}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <PaymentFormBlock
            className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0')}
            type={TransferRequestTypeEnum.BetweenInsigniaAccount}
            account={props.account}
            accountTo={accountTo}
            currency={props.account?.currency}
            reasonOptions={reasonOptions}
            onRequestFee={requestFeeHandler}
          />
          <FilesFormBlock
            className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0 pt-0')}
            transactionId={transferRequestsCreateStore.data?.id}
          />
        </form>
        <div className={cn('mt-3', PaletteUtils.isVcfcPalette && 'mt-0 rounded-b-xl bg-white p-6 pt-0 dark:bg-soft-black')}>
          <Button
            className={cn('max-w-72', PaletteUtils.isVcfcPalette && 'w-[285px]')}
            color='primary'
            type='submit'
            form={formId}
            disabled={feeStore.loading || formState.isSubmitting || transferRequestsCreateStore.loading || transferRequestsUpdateStore.loading}
          >
            {t('form.buttons.transfer')} {AmountUtils.transformToAmountWithCurrency(beneficiaryAmount, accountTo?.currency)}
          </Button>
        </div>
      </div>
      {transferRequestsCreateStore.data?.id && (
        <TransferConfirmationBlock
          showPaymentModal={showPaymentModal}
          onClosePaymentModal={() => setShowPaymentModal(false)}
          account={props.account!}
          accountTo={accountTo!}
        />
      )}
    </FormProvider>
  );
};
